import { ChangeEvent, useRef, useEffect } from 'react'
import { useMutation } from 'react-query'
import { useSelector } from 'react-redux'
import {
  UPLOAD_MESSAGES,
  processStreamEvent,
  showCompletionToast,
  closeController,
  getEventMessage,
} from './utils'
import { StreamEvent, StreamController } from './types'
import { useLoader } from '../../../providers'
import { useRoles } from '../../../utils/hooks/useRoles'
import { RootState } from '../../../store'

type UseSampleImporterParams = {
  uploadFileAndStream: any
  onSuccessCallback?: () => void
  prefix?: 'flour' | 'wheat' | 'baker'
}

export const useSampleImporter = ({
  uploadFileAndStream,
  onSuccessCallback,
  prefix = 'wheat',
}: UseSampleImporterParams) => {
  const { showLoader, hideLoader, setLoaderText, setProgress } = useLoader()
  const { isSuperAdmin } = useRoles()
  const profile = useSelector((state: RootState) => state.user.profile)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const loaderIdRef = useRef<string | null>(null)
  const controllerRef = useRef<StreamController | null>(null)

  const loaderControls = { setLoaderText, setProgress }

  const clearFileInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const cleanup = () => {
    closeController(controllerRef.current)
    controllerRef.current = null
    if (loaderIdRef.current) {
      hideLoader(loaderIdRef.current)
      loaderIdRef.current = null
    }
  }

  useEffect(() => {
    if (!isSuperAdmin || !profile) {
      cleanup()
    }
  }, [isSuperAdmin, profile?.company?.id])

  const hideLoaderAndClearRef = () => {
    hideLoader(loaderIdRef.current || undefined)
    loaderIdRef.current = null
  }

  const uploadMutation = useMutation(
    async ({ file, params }: { file: File; params?: Record<string, any> }) => {
      return await new Promise<StreamEvent & { isPartialSuccess?: boolean }>(
        (resolve, reject) => {
          let controller: StreamController | null = null
          const loaderId = loaderIdRef.current || undefined

          const closeStream = () => {
            closeController(controller)
            controller = null
            controllerRef.current = null
          }

          setLoaderText(UPLOAD_MESSAGES.preparing, loaderId)

          uploadFileAndStream(file, params || {}, {
            onOpen: () => setLoaderText(UPLOAD_MESSAGES.started, loaderId),
            onEvent: (evt: StreamEvent) => {
              processStreamEvent(
                evt,
                loaderControls,
                {
                  onComplete: (completedEvt, isPartialSuccess) => {
                    closeStream()
                    resolve({ ...completedEvt, isPartialSuccess })
                  },
                  onError: (errorEvt) => {
                    closeStream()
                    reject(errorEvt)
                  },
                },
                loaderId
              )
            },
            onError: (err: any) => {
              closeStream()
              reject(err)
            },
          })
            .then((res: { controller: StreamController }) => {
              controller = res.controller
              controllerRef.current = controller
            })
            .catch((err: any) => {
              closeStream()
              reject(err)
            })
        }
      )
    },
    {
      onSuccess: (data) => {
        const message = getEventMessage(data) || UPLOAD_MESSAGES.defaultSuccess
        showCompletionToast(message, data?.isPartialSuccess ?? false)
        onSuccessCallback?.()
        clearFileInput()
      },
      onError: (err: any) => {
        const detail =
          err?.detail || err?.message || UPLOAD_MESSAGES.defaultFailed
        console.error(detail)
      },
      onSettled: hideLoaderAndClearRef,
    }
  )

  const handleUpload = async (
    eventOrFile: ChangeEvent<HTMLInputElement> | File,
    additionalParams?: Record<string, any>
  ) => {
    const file =
      eventOrFile instanceof File ? eventOrFile : eventOrFile.target.files?.[0]
    if (!file) return

    const loaderId = `${prefix}-upload-${Date.now()}`
    loaderIdRef.current = loaderId
    showLoader('Uploading file...', loaderId, prefix)
    uploadMutation.mutate({ file, params: additionalParams })
  }

  return {
    handleUpload,
    fileInputRef,
    isUploading: uploadMutation.isLoading,
  }
}
